import { TablePagination } from '@mui/material';
import styled, { css } from 'styled-components';
import theme from 'v3/theme';
import { Text } from 'v3/components';

export const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 300px);
`;

export const FleetCellSkeleton = styled.div`
  width: 100%;
  height: 18px;
  border-radius: 4px;
  background: #e5e7eb;
`;

export const GridViewport = styled.div`
  height: calc(100vh - 340px);
  min-height: 460px;
  width: 100%;
  border: 1px solid #e5eaee;
  border-radius: 8px;
  overflow: hidden;

  .ag-theme-alpine {
    --ag-border-color: #e5eaee;
    --ag-row-border-color: #edf1f5;
    --ag-header-background-color: #f8fafc;
    --ag-odd-row-background-color: #fcfdff;
    --ag-font-size: 13px;
    --ag-font-family: Poppins, sans-serif;
    height: 100%;
    width: 100%;
  }

  .ag-theme-alpine .ag-header-cell {
    border-right: 1px solid #edf1f5;
  }

  .ag-theme-alpine .ag-header-cell-label {
    color: #4b5563;
    font-weight: 600;
  }

  .ag-theme-alpine .ag-cell {
    display: flex;
    align-items: center;
    border-right: 1px solid #f2f4f7;
    line-height: 1.35;
  }

  .ag-theme-alpine .ag-row {
    transition: background-color 120ms ease;
  }

  .ag-theme-alpine .ag-row-hover .ag-cell,
  .ag-theme-alpine .ag-row:hover .ag-cell {
    background-color: #f6f9fc;
  }

  .ag-theme-alpine .ag-cell-focus:not(.fleet-cell-status),
  .ag-theme-alpine .ag-cell:focus:not(.fleet-cell-status) {
    outline: none !important;
    box-shadow: inset 0 0 0 2px #2f80ed !important;
    background-color: rgba(47, 128, 237, 0.08);
    border-radius: 4px;
  }

  .ag-theme-alpine .ag-cell.ag-cell-focus:not(.fleet-cell-status),
  .ag-theme-alpine .ag-cell-focus-within:not(.fleet-cell-status) {
    outline: none !important;
    box-shadow: inset 0 0 0 2px #2f80ed !important;
    background-color: rgba(47, 128, 237, 0.08);
    border-radius: 4px;
  }

  .ag-theme-alpine .fleet-cell-status.ag-cell-focus,
  .ag-theme-alpine .fleet-cell-status.ag-cell-focus-within,
  .ag-theme-alpine .fleet-cell-status:focus {
    outline: none !important;
  }

  .ag-theme-alpine .fleet-cell-status {
    position: relative;
    transition: box-shadow 140ms ease, background-color 140ms ease;
    background-clip: padding-box;
  }

  .ag-theme-alpine .fleet-cell-content {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .ag-theme-alpine .fleet-cell-content-value {
    min-width: 0;
    flex: 1;
  }

  .ag-theme-alpine .fleet-cell-loader {
    margin-left: auto;
    flex-shrink: 0;
  }

  .ag-theme-alpine .fleet-cell-status-pending {
    box-shadow: none !important;
    background-color: transparent !important;
    border-radius: 4px;
  }

  .ag-theme-alpine .ag-body-horizontal-scroll-viewport::-webkit-scrollbar,
  .ag-theme-alpine .ag-body-vertical-scroll-viewport::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  .ag-theme-alpine .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb,
  .ag-theme-alpine .ag-body-vertical-scroll-viewport::-webkit-scrollbar-thumb {
    background-color: #b4c0cc;
    border-radius: 2px;
  }

  .ag-theme-alpine .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-track,
  .ag-theme-alpine .ag-body-vertical-scroll-viewport::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

export const StyledTablePagination = styled(TablePagination)`
  padding: 0 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  bottom: 0;
  z-index: 2;
  border-top: 0.5px solid rgba(0, 0, 0, 0.08);
  background-color: #fff;
  p {
    margin: 0;
  }
`;

export const ErrorText = styled(Text)`
  margin-bottom: 5px;
  ${(props) =>
    props.color === 'error' &&
    css`
      color: #f64e60 !important;
    `};
`;

export const StyledControl = styled.div`
  align-items: center;
  background-color: #fff;
  border: 1px solid #e5eaee;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  outline: 0 !important;
  position: relative;
  transition: all 100ms;
  font-family: Poppins;
  letter-spacing: 0.02em;
  min-height: 44px;
  font-size: 0.875rem;

  ${(props) =>
    props.children[0].props.isDisabled &&
    css`
      background-color: #e5eaee;
    `}

  ${(props) =>
    props.children[0].props.isMulti &&
    css`
      height: auto;
    `}

  ${({ error }) =>
    error &&
    css`
      border-color: #f64e60;
    `};

  @media (max-width: 768px) {
    height: 44px;
  }
`;

export const StyledValueContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  max-height: ${(props) =>
    props.selectValuesContainerMaxHeight ? props.selectValuesContainerMaxHeight : 'auto'};
  overflow-y: ${(props) =>
    props.selectValuesContainerOverflowY ? props.selectValuesContainerOverflowY : 'hidden'};
`;

export const StyledPlaceholder = styled.div`
  margin: 0 2px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.placeholder};
`;

export const StyledFleetCellContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;

  ${({ $status, theme }) =>
    $status === 'pending' &&
    css`
      background: ${theme.colors.warningLight};
    `}

  ${({ $status, theme }) =>
    $status === 'success' &&
    css`
    outline: none !important;
    box-shadow: inset 0 0 0 2px ${theme.colors.success} !important;
    border-radius: 4px;
    width: 100%;
    height: 100%;
    `}

  ${({ $status, theme }) =>
    $status === 'error' &&
    css`
      background: ${theme.colors.dangerLight};
    `}
`;

export const ClearButtonContainer = styled.button`
  opacity: 0;
  pointer-events: none;
  transition: opacity 120ms ease;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0 4px 0 auto;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  color: #94a3b8;

  &:hover {
    color: #475569;
  }

  .ag-row-hover &,
  .ag-row:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`;

export const BodyContainer = styled.div`
  padding: 0 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FooterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

export const StyledStatusIcon = styled.div`
  position: absolute;
  top: 7px;
  right: 3px;

  width: 20px;
  height: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background: white;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);

  z-index: 2;

  svg {
    width: 20px;
    height: 20px;

    color: ${({ $status, theme }) =>
    $status === 'success'
      ? theme.colors.success
      : theme.colors.danger};
  }
`;
